import { animate, keyframes, transition, trigger } from '@angular/animations';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute, Data } from '@angular/router';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { VideoDialogComponent } from 'src/app/components';
import { BreadcrumbService } from 'src/app/services';
import * as kf from '../../../core/keyframes';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['breadcrumb.component.scss'],
  animations: [
      trigger('cardAnimator', [
        transition('* => wobble', animate(1000, keyframes(kf.wobble))),
        transition('* => swing', animate(1000, keyframes(kf.swing))),
        transition('* => jello', animate(1000, keyframes(kf.jello))),
        transition('* => zoomOutRight', animate(1000, keyframes(kf.zoomOutRight))),
        transition('* => slideOutLeft', animate(1000, keyframes(kf.slideOutLeft))),
        transition('* => rotateOutUpRight', animate(1000, keyframes(kf.rotateOutUpRight))),
        transition('* => flipOutY', animate(1000, keyframes(kf.flipOutY))),
        transition('* => headShake', animate(1000, keyframes(kf.headShake)))
      ])
    ]
})
export class AppBreadcrumbComponent implements OnDestroy {
  @Input() pageInfo: Data = Object.create(null);
  originalData: Data = Object.create(null);
  animationState = 'swing';
  videos: any[] = [];

  childActivatedRoute: ActivatedRoute | undefined;

  _subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private breadcrumbService: BreadcrumbService,
    private dialog: MatDialog
  ) {
    this._subscriptions.push(this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .pipe(map(() => this.activatedRoute))
      .pipe(
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .pipe(filter(route => route.outlet === 'primary'))
      .pipe(tap((activatedRoute) => { this.childActivatedRoute = activatedRoute; }))
      .pipe(mergeMap(route => route.data))
      .subscribe(data => {
        this.originalData = data;
        this.pageInfo = {};
        this.videos = data.videos || [];
        this.resetSelectedIndex();
        this.processData(data);
      }));

      this._subscriptions.push(this.breadcrumbService.paramSet$.subscribe(() => {
        this.processData(this.originalData);
      }));
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  resetSelectedIndex() {
    this.currentVideoIndex = 0;
  }

  currentVideoIndex: number = 0;
  openVideoModal(): void {
    const dialogRef = this.dialog.open(VideoDialogComponent, {
      width: '80%',
      maxWidth: '800px',
      data: { 
        videoUrl: this.videos[this.currentVideoIndex].url, 
        videos: this.videos, 
        currentVideoIndex: this.currentVideoIndex 
      }
    });

    this._subscriptions.push(dialogRef.afterClosed().subscribe(result => {
      if (result?.next) {
        this.currentVideoIndex = result.next;
      }
    }));
  }

  private processData(data: any) {
    this.pageInfo['title'] = this.breadcrumbService.replaceParamsWithValues(data['title']);

    let title = "SituPortal";
    if (this.pageInfo['title']?.length > 0) {
      title += " - " + this.pageInfo['title'];
    }
    this.titleService.setTitle(title);

    this.pageInfo['urls'] = [];
    for (let i = 0; i < (data.urls as { title: string, url: string }[])?.length; i++) {
      const url = (data.urls as { title: string, url: string }[])[i];
      this.pageInfo.urls.push({
        title: this.breadcrumbService.replaceParamsWithValues(url.title),
        url: url.url ? this.getProcessedUrl(url.url) : undefined
      });
    }
  }

  private getProcessedUrl(url: string) {
    return this.router.createUrlTree([url], { relativeTo: this.childActivatedRoute ?? this.activatedRoute }).toString();
  }

  navigateByUrl(url: string) {
    if (!url) {
      return;
    }

    this.router.navigateByUrl(url);
  }
}
